// src/components/aols/aeo/Venus/Venus.tsx
import React, { useState, useEffect, useCallback } from "react";
import { Layout, Typography, Button, Tooltip } from "antd";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import logo from "/logo.jpg";
import  Footer  from "../../../dev/utils/Footer/Footer";
import "./Venus.css";

const { Title, Paragraph } = Typography;

// Define the type for the dimensions state
interface Dimensions {
  width: number;
  height: number;
}

const Venus: React.FC = () => {
  const [dimensions, setDimensions] = useState<Dimensions>({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    const handleResize = () => setDimensions({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogoClick = useCallback(() => {
    window.location.href = "https://www.olsme.com";
  }, []);

  return (
    <Layout className="full-height-layout" style={{ background: "#000000" }}>
      <Helmet>
        <title>Venus Mission | OLS Space Exploration with Light Science</title>
        <meta
          name="description"
          content="Join the Venus Mission by onelightsystem OLS iee.aeo, led by Asvitloaten. Explore starship design, orbit-station plans, and Light Science for sustainable space settlement."
        />
        <meta
          name="keywords"
          content="Venus Mission, OLS iee.aeo, Asvitloaten, Sun Light Meditation, Light Science, space exploration, Venus settlement, starship design, orbit-station, onelightsystem OLS"
        />
        <meta name="author" content="onelightsystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Venus Mission | OLS Space Exploration with Light Science" />
        <meta
          property="og:description"
          content="Discover the Venus Mission by OLS iee.aeo, pioneering starship technology and Light Science for Venus exploration and settlement."
        />
        <meta property="og:url" content="https://www.olsme.com/venus" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <link rel="canonical" href="https://www.olsme.com/venus" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ResearchProject",
            "name": "Venus Mission",
            "url": "https://www.olsme.com/venus",
            "description": "The Venus Mission by onelightsystem OLS iee.aeo, led by Asvitloaten, advances space exploration through starship design, orbit-station development, and Light Science research for Venus settlement.",
            "founder": {
              "@type": "Person",
              "name": "Nazar Pankiv",
              "alternateName": "Asvitloaten",
            },
            "parentOrganization": {
              "@type": "Organization",
              "name": "onelightsystem OLS",
              "url": "https://www.olsme.com",
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Belmont",
              "addressRegion": "CA",
              "addressCountry": "US",
            },
            "startDate": "2023-05-01",
            "keywords": "Venus exploration, space settlement, Light Science",
          })}
        </script>
      </Helmet>

      <Layout.Header
        className="content-section"
        style={{
          textAlign: "center",
          padding: "40px 20px",
          background: "rgba(255, 204, 0, 0.1)",
          position: "relative",
          zIndex: 100,
        }}
      >
        <Tooltip title="Visit OLS Main Site">
          <Link to="/" onClick={handleLogoClick}>
            <img
              src={logo}
              alt="onelightsystem OLS Logo"
              className="logo"
              aria-label="OLS Logo"
              loading="lazy"
            />
          </Link>
        </Tooltip>
        <Title level={1} className="section-title" style={{ marginBottom: "20px" }}>
          onelightsystem OLS iee.aeo
        </Title>
        <Title level={2} className="subsection-title">
          Venus Exploration Mission
        </Title>
      </Layout.Header>

      <Layout.Content className="content-section">
        <div className="content">
          <Paragraph className="content-text">
            Advancing Space Exploration with Light Science and Innovation
          </Paragraph>

          <div className="feature-card">
            <Title level={3} className="subsection-title">Mission Overview</Title>
            <Paragraph className="content-text">
              The Venus Mission, led by Asvitloaten at onelightsystem OLS iee.aeo, is a pioneering effort to explore and settle Venus. 
              By integrating starship technology, orbit-station development, and OLS Light Science, we aim to unlock Venus’s potential as a future human frontier.
            </Paragraph>
            <Paragraph className="content-text">
              <strong>Key Objectives:</strong>
              <ul className="styled-list">
                <li>Develop starships resilient to Venus’s harsh environment.</li>
                <li>Establish a sustainable orbit-station for ongoing research.</li>
                <li>Investigate Venus’s habitability using Sun Light Meditation and Light Science.</li>
                <li>Design a landing base for future Venus settlements.</li>
              </ul>
              <a href="mailto:info@olsme.com">Contact us</a> to join as an OLS student and contribute to this interstellar journey!
            </Paragraph>
          </div>

          <div className="feature-card">
            <Title level={3} className="subsection-title">Project Timeline</Title>
            <Paragraph className="content-text">
              Our roadmap to Venus, updated as of March 26, 2025:
              <ul className="styled-list">
                <li><strong>Planning Simulations:</strong></li>
                <li>May-Aug 2023: Completed initial feasibility and atmospheric modeling.</li>
                <li>Mar 2025: In progress—orbit-station and landing logistics simulations.</li>
                <li>Oct 25–Dec 9, 2026: Scheduled simulations and launch testing.</li>
                <li>Apr 17–Jul 16, 2028: Further modeling and launch preparations.</li>
                <li>Jan 1–Feb 16, 2030: Starship and orbit-station prototype testing.</li>
                <li>Aug 5–Sep 20, 2031: Pre-launch crew training.</li>
                <li><strong>Launch: Mar 9–Apr 23, 2033:</strong> Deploy starship and orbit-station to Venus.</li>
              </ul>
              Stay tuned for updates on this historic mission!
            </Paragraph>
          </div>

          <div className="feature-card">
            <Title level={3} className="subsection-title">Light Science Insights</Title>
            <Paragraph className="content-text">
              OLS Light Science posits that Venus and Earth share a common origin as cooled celestial bodies. 
              We explore the theory of a lost Venusian civilization, using Sun Light Meditation to guide our research into its past and future habitability.
            </Paragraph>
          </div>

          <div className="feature-card">
            <Title level={3} className="subsection-title">Join as an OLS Student</Title>
            <Paragraph className="content-text">
              Get involved in the Venus Mission:
              <ul className="styled-list">
                <li>Participate in live mission discussions.</li>
                <li>Experience cutting-edge simulations.</li>
                <li>Apply Light Science to space exploration.</li>
              </ul>
              <Button className="custom-button">
                <Link to="/register/olssregister">Enroll Today</Link>
              </Button>
            </Paragraph>
          </div>

          <div className="feature-card">
            <Title level={3} className="subsection-title">Partner with Us</Title>
            <Paragraph className="content-text">
              Collaborate on this groundbreaking mission: <a href="mailto:info@olsme.com">info@olsme.com</a>.
            </Paragraph>
          </div>

          <div className="button-group">
            <Button className="custom-button">
              <Link to="/register/olssregister">OLS Student Application</Link>
            </Button>
            <Button className="custom-button">
              <Link to="/register/olsmeregister">OLSME Subscription</Link>
            </Button>
            <Button className="custom-button">
              <Link to="/register/olslmregister">OLSLM Assets</Link>
            </Button>
          </div>
        </div>
      </Layout.Content>

      <Layout.Footer>
        <Footer />
      </Layout.Footer>
    </Layout>
  );
};

export default Venus;